import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ParameterService } from '../../../shared/services/parameter/parameter.service';
import { WorklistService } from '../worklist.service';
import { AppMessageService } from '../../../shared/services/app-message/app-message.service';

interface RecurrenceRule {
  type: 'daily' | 'weekly' | 'monthly';
  intervalValue: number;
  daysOfWeek?: number[];
  dayOfMonth?: number;
  endDate?: Date;
  occurrenceCount?: number;
}

@Component({
  selector: 'app-worklist-create',
  templateUrl: './worklist-create.component.html',
  styleUrls: ['./worklist-create.component.scss'],
})
export class WorklistCreateComponent implements OnInit {
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() workCreated = new EventEmitter<any>();

  workForm: FormGroup;
  recurrenceForm: FormGroup;

  teamsOptions: any[] = [];
  priorityOptions: any[] = [];
  showRecurrencePanel = false;
  recurrenceSchedule: any[] = [];
  loading = false;

  minWorkDate: Date | null = null;
  constructor(
    private readonly fb: FormBuilder,
    private readonly parameterService: ParameterService,
    private readonly service: WorklistService,
    private readonly messageService: AppMessageService
  ) {
    this.workForm = this.fb.group({
      title: ['', [Validators.required, Validators.maxLength(255)]],
      description: ['', [Validators.maxLength(250)]],
      workDate: [null, Validators.required],
      alarmTime: [null,Validators.required],
      priorityId: [null, ],
      teamIds: [[], Validators.required],
      isRecurring: [false],
    },
    {
    validators: [workDateAfterAlarmTimeValidator()] // Burada validator'ı ekledik
    });

    this.recurrenceForm = this.fb.group({
       endDate: [null ],
         intervalValue: [1],
    });

    this.workForm.get('isRecurring')?.valueChanges.subscribe((value) => {
      this.showRecurrencePanel = value;
      if (value) this.generateRecurrenceSchedule();
      else this.recurrenceSchedule = [];
    });

    this.recurrenceForm.valueChanges.subscribe(() => {
      if (this.workForm.get('isRecurring')?.value) {
        this.generateRecurrenceSchedule();
      }
    });
  }

  ngOnInit(): void {
    this.loadParameterOptions();
  }

  loadParameterOptions() {
    this.parameterService.getParameterValues('data-group', 'teams').subscribe((res) => {
      this.teamsOptions = res?.datas?.values.map((data: any) => ({
        label: data.valueObject.name.value,
        value: data?.valueObject.id.value,
      }));
    });

    this.parameterService.getParameterValues('data-group', 'priority').subscribe((res) => {
      this.priorityOptions = res?.datas?.values.map((data: any) => ({
        label: data.valueObject.name.value,
        value: data?.valueObject.id.value,
      }));
    });
  }

  generateRecurrenceSchedule() {
  const workDate = this.workForm.get('workDate')?.value;
  const alarmTime = this.workForm.get('alarmTime')?.value;
  const endDate = this.recurrenceForm.get('endDate')?.value;
  const interval = this.recurrenceForm.get('intervalValue')?.value;

  if (!workDate || !endDate || !interval || !alarmTime) {
    this.recurrenceSchedule = [];
    return;
  }

  this.recurrenceSchedule = [];

  const baseWorkDate = new Date(workDate);
  const baseAlarmTime = new Date(alarmTime);
  const timeDiffMs = baseWorkDate.getTime() - baseAlarmTime.getTime();

  let currentWorkDate = new Date(baseWorkDate);
  const end = new Date(endDate);

  while (currentWorkDate <= end) {
    const currentAlarmTime = new Date(currentWorkDate.getTime() - timeDiffMs);

    this.recurrenceSchedule.push({
      startDate: new Date(currentWorkDate),
      endDate: new Date(currentAlarmTime),
    });

    currentWorkDate.setDate(currentWorkDate.getDate() + interval);
  }
}

  removeScheduleItem(index: number) {
    this.recurrenceSchedule.splice(index, 1);
  }

  onSubmit() {
  if (this.workForm.invalid || this.recurrenceForm.invalid) {
    this.workForm.markAllAsTouched();
    this.recurrenceForm.markAllAsTouched();
    return;
  }

  const formData = {
    title: this.workForm.value.title,
    description: this.workForm.value.description,
    teams: this.workForm.value.teamIds, // array of numbers
    workDate: this.workForm.value.workDate,
    alarmClokDate: this.workForm.value.alarmTime,
    priority: this.workForm.value.priorityId,
    repetitiveWork: this.workForm.value.isRecurring,
    recurrenceRule: this.workForm.value.isRecurring ? this.recurrenceForm.value : null,
  };

  this.loading = true;
  this.service.create(formData).subscribe({
    next: (res:any) => {
      this.workCreated.emit(res.data);
      this.messageService.successToast("", res.message);
      this.resetForm();
      this.loading = false;

    },
    error: (err) => {
      const message =
        err?.error?.message ??
        err?.message ??  ''
      this.messageService.errorToast("", message);
      this.loading = false;
    }

  });
}


  resetForm() {
    this.workForm.reset();
    this.recurrenceForm.reset();
    this.showRecurrencePanel = false;
    this.recurrenceSchedule = [];

    this.workForm.patchValue({
      isRecurring: false,
    });

    this.recurrenceForm.patchValue({
      type: 'daily',
      intervalValue: 1,
    });
    this.visibleChange.emit(false);

  }

  onCancel() {
    this.resetForm();
    this.visibleChange.emit(false);
  }

  onAlarmTimeChange() {
    const alarmTime = this.workForm.get('alarmTime')?.value;
    if (alarmTime) {
      this.minWorkDate = new Date(alarmTime); // tarih + saat
    }
  }
  
  get workDateBeforeAlarmTimeError(): boolean {
    return !!(
      this.workForm.errors?.['workBeforeAlarm'] &&
      (this.workForm.get('workDate')?.touched ?? this.workForm.get('alarmTime')?.touched)
    );
  }

}

export function workDateAfterAlarmTimeValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const workDate = control.get('workDate')?.value;
    const alarmTime = control.get('alarmTime')?.value;

    if (workDate && alarmTime) {
      const workDateTime = new Date(workDate).getTime();
      const alarmDateTime = new Date(alarmTime).getTime();

      if (workDateTime < alarmDateTime) {
        return { workBeforeAlarm: true };
      }
    }
    return null;
  };
}